/**
 * `/$org/apps/probe/practice`: Probe's Home. How ready each examination area
 * is, the actions the interviews left open, and the interviews: the areas
 * listed on the left, the selected one's detail on the right
 * (`home/ProbeHomeSplit`). `?area=<area>` selects one area;
 * `?show=interviews` opens every interview.
 *
 * @module components/apps/probe/ProbeHomePage
 */
import { useSearch } from "@tanstack/react-router";
import { useMemo } from "react";

import { PageHeader } from "../../regulation/PageHeader";
import { probeHomeOf, type HomeShow } from "./home/probeHome";
import { StartButton } from "./home/ProbeHomeParts";
import { ProbeHomeSplit } from "./home/ProbeHomeSplit";
import { useActionsDone } from "./probeActionsDone";
import { areaFromSlug, areaSlug } from "./probeArea";
import { useProbeArea } from "./ProbeAreaProvider";
import { useProbeInterviews } from "./probeInterviewsStore";
import { ProbeLoadError, ProbeRowsSkeleton } from "./ProbeParts";

const SHOWS: ReadonlyArray<HomeShow> = ["interviews"];

/** What `?show=` asks for, or null for anything else. */
export const homeShowOf = (value: unknown): HomeShow | null =>
  SHOWS.find((show) => show === value) ?? null;

export function ProbeHomePage() {
  const { organizationId, orgSegment } = useProbeArea();
  const search: { readonly area?: unknown; readonly show?: unknown } = useSearch({ strict: false });
  const focus = areaFromSlug(typeof search.area === "string" ? search.area : null);
  const show = homeShowOf(search.show);
  const { status, interviews, reload } = useProbeInterviews(organizationId);
  const isDone = useActionsDone();
  const view = useMemo(() => probeHomeOf(interviews, focus, isDone), [interviews, focus, isDone]);

  return (
    <>
      <PageHeader title="Home" actions={<StartButton orgSegment={orgSegment} focus={focus} />} />
      <div className="flex min-h-0 flex-1 flex-col" data-probe-home={focus === null ? "" : areaSlug(focus)}>
        {status === "loading" ? (
          <ProbeRowsSkeleton rows={10} />
        ) : status === "error" ? (
          <ProbeLoadError what="Your interviews" onRetry={reload} />
        ) : (
          <ProbeHomeSplit view={view} orgSegment={orgSegment} show={show} />
        )}
      </div>
    </>
  );
}
